/**
 * The recorded room's engine: the talk's video and everything that follows its playhead. Taken from the
 * player the room had before the redesign, so the talk plays, counts and pauses for moments exactly as it did:
 *
 * - HLS through hls.js first, the browser's own player only as the fallback (a browser can claim native HLS and
 *   then fail to play it), with the media token on the master request only.
 * - The playhead posted every five seconds, on pause and at the end, so "watched" and "completed" stay true.
 * - A moment fires only when the talk PLAYS across its second, never on a seek (`arrivals.onTime`).
 * - A moment that holds the talk pauses it, and the talk starts again only if the hold was what stopped it.
 */
import { type RefObject, useCallback, useEffect, useRef, useState } from "react";
import type { WatchSession } from "../../../shared/types";
import type { Arrivals } from "../TalkPlayer";
import { holdsTalk } from "./talk";

/** How often the playhead is posted while the talk plays. */
const PROGRESS_EVERY_SEC = 5;
/** The speed the viewer picked, kept in this browser across talks. */
export const SPEED_KEY = "fw.watch.rate";

/** The media element's own readyState once it knows the stream's length (HTMLMediaElement.HAVE_METADATA). */
const HAVE_METADATA = 1;

function savedSpeed(): number {
  try {
    const kept = Number(localStorage.getItem(SPEED_KEY));
    return [1, 1.25, 1.5, 1.75, 2].includes(kept) ? kept : 1;
  } catch {
    return 1;
  }
}

export interface RoomVideo {
  videoRef: RefObject<HTMLVideoElement | null>;
  /** Where the playhead is, in seconds. */
  cur: number;
  total: number;
  playing: boolean;
  ended: boolean;
  failed: boolean;
  muted: boolean;
  speed: number;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  /**
   * Move the talk. A jump crosses nothing, so no moment fires for the seconds it skips, and the moment on screen
   * goes without starting the talk again.
   */
  seek: (sec: number) => void;
  setSpeed: (s: number) => void;
  toggleMute: () => void;
  /** Start again from the beginning after the end. */
  restart: () => void;
  /** The `<video>` element's events. */
  events: {
    onLoadedMetadata: () => void;
    onDurationChange: () => void;
    onTimeUpdate: () => void;
    onPlay: () => void;
    onPause: () => void;
    onEnded: () => void;
  };
}

export function useRoomVideo({
  session,
  arrivals,
  onProgress,
}: {
  session: WatchSession;
  arrivals: Arrivals;
  onProgress: (sec: number, pct: number) => void;
}): RoomVideo {
  const url = session.playback_url;
  const token = session.media_token;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const seekedToSaved = useRef(false);
  const lastPosted = useRef(0);
  const lastTick = useRef(session.watched_sec);
  const lastTickAt = useRef(Date.now());
  /** Did the room pause the talk for a moment? Then the room is the one to start it again. */
  const heldForMoment = useRef(false);
  /**
   * A jump or a play asked for before the stream has loaded. Why kept: the stream attaches a moment after the page
   * draws, and a press in that moment found no source, so the talk sat still at the second it was sent to.
   */
  const wanted = useRef<{ at: number | null; play: boolean }>({ at: null, play: false });
  const [cur, setCur] = useState(session.watched_sec);
  const [dur, setDur] = useState(session.duration_sec || 0);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [muted, setMuted] = useState(false);
  const [speed, setSpeedState] = useState(savedSpeed);
  const total = dur || session.duration_sec || 1;

  useEffect(() => {
    const video = videoRef.current;
    if (video) video.playbackRate = speed;
  }, [speed]);

  // The source: hls.js first, the browser's own HLS for Safari-family browsers without MSE.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url) return;
    let hlsInstance: { destroy: () => void } | null = null;
    let cancelled = false;
    const src = token ? `${url}${url.includes("?") ? "&" : "?"}t=${encodeURIComponent(token)}` : url;
    const native = () => {
      if (token) video.crossOrigin = "use-credentials";
      video.src = src;
    };
    void import("hls.js")
      .then(({ default: Hls }) => {
        if (cancelled) return;
        if (Hls.isSupported()) {
          const hls = new Hls(
            token
              ? { enableWorker: true, xhrSetup: (xhr: XMLHttpRequest) => { xhr.withCredentials = true; } }
              : { enableWorker: true },
          );
          // One recovery each before giving up: a network error reloads the source (which re-installs the media
          // cookie from `?t=`), a media error asks hls.js to recover. A second fatal of either kind is the end.
          let retriedNetwork = false;
          let retriedMedia = false;
          hls.on(Hls.Events.ERROR, (_e, data) => {
            if (!data.fatal) return;
            if (data.type === Hls.ErrorTypes.NETWORK_ERROR && !retriedNetwork) {
              retriedNetwork = true;
              const at = video.currentTime;
              hls.loadSource(src);
              hls.startLoad(at);
              return;
            }
            if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !retriedMedia) {
              retriedMedia = true;
              hls.recoverMediaError();
              return;
            }
            setFailed(true);
          });
          hls.loadSource(src);
          hls.attachMedia(video);
          hlsInstance = hls;
        } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
          native();
        } else {
          setFailed(true);
        }
      })
      .catch(() => {
        if (video.canPlayType("application/vnd.apple.mpegurl")) native();
        else setFailed(true);
      });
    return () => {
      cancelled = true;
      hlsInstance?.destroy();
    };
  }, [url, token]);

  const flush = useCallback(
    (sec: number) => {
      onProgress(sec, (sec / total) * 100);
      lastPosted.current = sec;
    },
    [total, onProgress],
  );

  const onLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video) return;
    if (Number.isFinite(video.duration) && video.duration > 0) setDur(video.duration);
    const want = wanted.current;
    if (want.at !== null || want.play) {
      seekedToSaved.current = true;
      if (want.at !== null) video.currentTime = want.at;
      const go = want.play;
      wanted.current = { at: null, play: false };
      if (go) {
        void video.play().catch(() => {
          /* a browser that refuses to start leaves the big play button there */
        });
      }
      return;
    }
    if (!seekedToSaved.current && session.watched_sec > 0) {
      seekedToSaved.current = true;
      try {
        video.currentTime = session.watched_sec;
      } catch {
        /* seeking before the video is ready is a no-op */
      }
    }
  };

  const onTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;
    const now = video.currentTime;
    const from = lastTick.current;
    const at = Date.now();
    const wall = (at - lastTickAt.current) / 1000;
    lastTick.current = now;
    lastTickAt.current = at;
    setCur(now);
    if (!video.seeking) arrivals.onTime({ from, to: now, playing: !video.paused, wall, rate: video.playbackRate });
    if (Math.abs(now - lastPosted.current) >= PROGRESS_EVERY_SEC) flush(now);
  };

  // The talk waits behind a moment that asks it to, and starts again after, only if the hold stopped it.
  const held = holdsTalk(arrivals);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (held) {
      if (!video.paused) {
        heldForMoment.current = true;
        video.pause();
      }
      return;
    }
    if (!heldForMoment.current) return;
    heldForMoment.current = false;
    void video.play().catch(() => {
      /* the browser would not start it again; the controls still work */
    });
  }, [held]);

  const onPlay = () => {
    const video = videoRef.current;
    if (held && video) {
      heldForMoment.current = true;
      video.pause();
      return;
    }
    setEnded(false);
    setPlaying(true);
  };

  const play = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.readyState < HAVE_METADATA) wanted.current.play = true;
    void video.play().catch(() => {
      /* a browser that refuses to start leaves the big play button there, unless the stream is still on its way */
    });
  }, []);
  // Why it drops a kept play: a play then a pause pressed before the stream is in must leave the talk paused.
  const pause = useCallback(() => {
    wanted.current.play = false;
    videoRef.current?.pause();
  }, []);
  const toggle = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) play();
    else pause();
  }, [play, pause]);

  const seek = useCallback(
    (sec: number) => {
      const video = videoRef.current;
      const to = Math.max(0, Math.min(total - 0.5, sec));
      if (arrivals.up) {
        heldForMoment.current = false;
        arrivals.dismiss(arrivals.up.id);
      }
      if (video) {
        if (video.readyState < HAVE_METADATA) wanted.current.at = to;
        video.currentTime = to;
      }
      lastTick.current = to;
      lastTickAt.current = Date.now();
      setCur(to);
      setEnded(false);
      flush(to);
    },
    [total, flush, arrivals],
  );

  const setSpeed = useCallback((s: number) => {
    setSpeedState(s);
    try {
      localStorage.setItem(SPEED_KEY, String(s));
    } catch {
      /* a browser that keeps nothing still plays at the chosen speed */
    }
  }, []);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
  }, []);

  const restart = useCallback(() => {
    seek(0);
    play();
  }, [seek, play]);

  return {
    videoRef,
    cur,
    total,
    playing,
    ended,
    failed,
    muted,
    speed,
    play,
    pause,
    toggle,
    seek,
    setSpeed,
    toggleMute,
    restart,
    events: {
      onLoadedMetadata,
      onDurationChange: onLoadedMetadata,
      onTimeUpdate,
      onPlay,
      onPause: () => {
        setPlaying(false);
        const video = videoRef.current;
        if (video) flush(video.currentTime);
      },
      onEnded: () => {
        setPlaying(false);
        setEnded(true);
        onProgress(Math.round(total), 100);
      },
    },
  };
}
